import React, { useState, useEffect, useCallback } from "react";
import { useRecoilState } from "recoil";
import { danceStepsAtom } from "../../Recoil/atoms";
import { FaPlay, FaPause } from "react-icons/fa";

const StepsComponent = ({
  setCurrentStep,
  setOnStepComplete,
  setIsInStepSequence,
  frameRate = 30, // Default to 30fps if not provided
  playing,
  setPlaying,
  onClearSelection, // <-- Add this prop
}) => {
  const [showSteps, setShowSteps] = useState(false);
  const [danceSteps, setDanceSteps] = useRecoilState(danceStepsAtom);
  const [selectedSteps, setSelectedSteps] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);

  // Debug log for dance steps
  useEffect(() => {
    console.log("🎬 StepsComponent received dance steps:", {
      count: danceSteps?.length || 0,
      steps: danceSteps,
    });
  }, [danceSteps]);

  // Clear selectedSteps when danceSteps changes (i.e., new video loaded)
  useEffect(() => {
    setSelectedSteps([]);
  }, [danceSteps]);

  const toggleSteps = () => {
    setShowSteps(!showSteps);
  };

  const handleStepSelection = (step, index) => {
    setSelectedSteps((prev) => {
      const isSelected = prev.some((s) => s.newStepId === step.newStepId);
      let newSelection;
      if (isSelected) {
        // If deselecting, only allow deselecting from the end
        if (prev[prev.length - 1].newStepId === step.newStepId) {
          newSelection = prev.slice(0, -1);
        } else {
          return prev;
        }
      } else {
        // If selecting, only allow selecting consecutive steps
        if (prev.length === 0) {
          newSelection = [step];
        } else {
          const lastSelected = prev[prev.length - 1];
          if (Math.abs(step.newStepId - lastSelected.newStepId) === 1) {
            newSelection = [...prev, step];
          } else {
            return prev;
          }
        }
      }
      // If selection is cleared, notify parent
      if (newSelection.length === 0 && onClearSelection) {
        onClearSelection();
      }
      return newSelection;
    });
  };

  const togglePlay = () => {
    if (selectedSteps.length === 0) return;

    if (!isPlaying) {
      // Combine all selected steps into one continuous playback
      const sortedSteps = [...selectedSteps].sort(
        (a, b) => a.newStepId - b.newStepId
      );

      // Create a combined step from first to last
      const firstStep = sortedSteps[0];
      const lastStep = sortedSteps[sortedSteps.length - 1];

      const combinedStep = {
        keyFrameIn: firstStep.keyFrameIn,
        keyFrameOut: lastStep.keyFrameOut,
        newStepId: firstStep.newStepId,
        // Add metadata to show this is a combined step
        isCombined: true,
        originalSteps: sortedSteps,
      };

      console.log("🎬 Starting combined playback:", {
        totalSteps: sortedSteps.length,
        startFrame: firstStep.keyFrameIn,
        endFrame: lastStep.keyFrameOut,
        duration: `${(
          ((lastStep.keyFrameOut - firstStep.keyFrameIn) / frameRate) *
          1000
        ).toFixed(0)}ms`,
        selectedSteps: sortedSteps.map((step, index) => ({
          stepNumber: index + 1,
          stepId: step.newStepId,
          frames: `${step.keyFrameIn} - ${step.keyFrameOut}`,
        })),
      });

      setIsPlaying(true);
      setIsInStepSequence(true);
      setPlaying(true);

      // Play the combined step
      setCurrentStep(combinedStep);
    } else {
      console.log("⏸️ Stopping playback");
      setIsPlaying(false);
      setIsInStepSequence(false);
      setPlaying(false);
      setCurrentStep(null);
    }
  };

  const handleDragStart = (e, draggedIndex) => {
    e.dataTransfer.setData("draggedIndex", draggedIndex);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const draggedIndex = parseInt(e.dataTransfer.getData("draggedIndex"));

    // Only allow merging with adjacent steps
    if (Math.abs(draggedIndex - targetIndex) !== 1) return;

    const newSteps = [...danceSteps];
    const step1 = newSteps[Math.min(draggedIndex, targetIndex)];
    const step2 = newSteps[Math.max(draggedIndex, targetIndex)];

    const mergedStep = {
      keyFrameIn: Math.min(step1.keyFrameIn, step2.keyFrameIn),
      keyFrameOut: Math.max(step1.keyFrameOut, step2.keyFrameOut),
      newStepId: Math.min(step1.newStepId, step2.newStepId),
    };

    newSteps.splice(Math.min(draggedIndex, targetIndex), 2, mergedStep);

    const reindexed = newSteps.map((step, i) => ({
      ...step,
      newStepId: i,
    }));

    setDanceSteps(reindexed);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <div className="steps-container relative">
      <button
        className="w-auto bg-gray-800/80 hover:bg-gray-700/80 transition-colors text-white py-2 px-4 text-base rounded-[5px] cursor-pointer whitespace-nowrap m-[10px]"
        onClick={toggleSteps}
      >
        STEPS
      </button>

      {showSteps && (
        <div className="steps-list absolute top-[50px] right-[10px] w-[200px] max-h-[60vh] overflow-y-auto bg-gray-800/90 p-2 rounded-md shadow-lg z-50">
          {selectedSteps.length > 0 && (
            <button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md mb-2 flex items-center justify-center gap-2"
              onClick={togglePlay}
            >
              {isPlaying ? <FaPause /> : <FaPlay />}
              {isPlaying ? "Pause" : "Play"}
            </button>
          )}
          {danceSteps.map((step, index) => (
            <div
              key={index}
              className="step-item bg-gray-200 text-black py-1 px-2 m-2 rounded-md shadow-md cursor-pointer hover:bg-gray-300 transition-colors flex items-center justify-between"
            >
              <div className="flex-grow" onClick={() => setCurrentStep(step)}>
                <p className="font-semibold">Step {index + 1}</p>
                <p className="text-sm">
                  {step.keyFrameIn} - {step.keyFrameOut} frames
                </p>
              </div>
              <div
                className={`w-5 h-5 border-2 rounded cursor-pointer flex items-center justify-center transition-all duration-200 ${
                  selectedSteps.some((s) => s.newStepId === step.newStepId)
                    ? "bg-blue-600 border-blue-600"
                    : "bg-white border-gray-400 hover:border-blue-400"
                }`}
                onClick={() => handleStepSelection(step, index)}
              >
                {selectedSteps.some((s) => s.newStepId === step.newStepId) && (
                  <svg
                    className="w-3 h-3 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StepsComponent;
